import React, { useEffect } from 'react';
import { Field, reduxForm, formValueSelector, change } from 'redux-form';
import moment from 'moment';
import momentLocalizer from 'react-widgets-moment';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { TafInput } from '../../../common/TafInput/TafInput';
import FormCheckbox from '../../../common/FormCheckbox/FormCheckbox';
import { validate } from '../Validator/FormContentValidatior';
import { passDigitsOnlyOnKeyPress, calculateInsuranceEndDate } from '../../../utils';
import TafTooltip from '../../../common/TafTooltip/TafTooltip';
import 'react-widgets/dist/css/react-widgets.css';
import './FormContent.scss';
import {
  InsuranceEndDateInfo,
  DisabilityAssessmentInfo,
  DisabilityPaymentPeriodInfo,
  DisabilityPeriodInfo,
  PartlyDisabledInfo,
  MonthlyPaymentInfo,
  UnemploymentInsuranceInfo,
  UnemploymentPeriodInfo,
} from '../../../common/TafTooltip/InfoText/index';
import {
  triggerDisabilityAssessmentInfoAction,
  triggerDisabilityPeriodInfoAction,
  triggerInsuranceStartDateInfoAction,
  triggerMonthlyPaymentInfoAction,
  triggerPartlyDisabledInfoAction,
  triggerPaymentPeriodInfoAction,
  triggerUnemploymentInsuranceInfoAction,
  triggerUnemploymentPeriodInfoAction,
  validationErrorAction,
} from '../FormContainer/ducks';
import
{
  DD_MM_YYYY_UI_FORMAT,
  MONTH,
  MONTHS,
} from '../../../timeConstants';

import {
  residualPeriods,
  allowanceTypes,
  unemploymentPeriods,
  disabilityRates,
  insurancePeriods,
  YES,
  FORM_NAME,
  UNEMPLOYMENT,
  BIRTHDATE,
  DISABILITY_VALUE,
  UNEMPLOYMENT_VALUE,
} from './enums/formEnums';
import { renderDateTimePicker,
  renderDropdownList,
  renderSelectList } from './renders';


momentLocalizer(moment);

// eslint-disable-next-line import/no-mutable-exports
let FormContent = ({ handleSubmit }) => {
  const nextMonthFirstDay = moment().startOf(MONTH)
    .add(1, MONTHS);
  const maxPossibleDate = new Date(new Date().setMonth(new Date().getMonth() + 12, 1));
  const mlbSummaryFormSelector = formValueSelector(FORM_NAME);
  const isUnemploymentSelected = useSelector((state) => mlbSummaryFormSelector(state, UNEMPLOYMENT));
  const isBirthDateSelected = useSelector((state) => mlbSummaryFormSelector(state, BIRTHDATE));
  const endDate = isBirthDateSelected ? calculateInsuranceEndDate(isBirthDateSelected)
    .format(DD_MM_YYYY_UI_FORMAT) : null;
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(change(FORM_NAME, 'endDate', endDate));
  }, [dispatch, endDate, isBirthDateSelected]);
  const isMonthlyPaymentClicked = useSelector((state) => state.mlbSummary.isMonthlyPaymentInfoClicked);
  const isDisabilityAssessmentInfo = useSelector((state) => state.mlbSummary.isDisabilityAssessmentInfo);
  const isPaymentPeriodInfo = useSelector((state) => state.mlbSummary.isPaymentPeriodInfo);
  const isDisabilityPeriodInfo = useSelector((state) => state.mlbSummary.isDisabilityPeriodInfo);
  const isInsuranceEndDateInfo = useSelector((state) => state.mlbSummary.isInsuranceEndDateInfo);
  const isPartlyDisabledInfo = useSelector((state) => state.mlbSummary.isPartlyDisabledInfo);
  const isUnemploymentInsuranceInfo = useSelector((state) => state.mlbSummary.isUnemploymentInsuranceInfo);
  const isUnemploymentPeriodInfo = useSelector((state) => state.mlbSummary.isUnemploymentPeriodInfo);
  const disabilityValue = useSelector((state) => mlbSummaryFormSelector(state, DISABILITY_VALUE));
  useEffect(() => {
    dispatch(change(FORM_NAME, UNEMPLOYMENT_VALUE, disabilityValue));
  }, [dispatch, disabilityValue]);
  const onlyDigits = (e) => passDigitsOnlyOnKeyPress(e);
  return (
    <form className="form" onSubmit={handleSubmit}>
      <div className="row radio-block">
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <h2>Welke dekking wilt u berekenen?</h2>
        <div className="radio-block-wrapper">
          <Field
            name="verzekeren"
            component={renderSelectList}
            data={['Arbeidsongeschiktheid']}
            defaultValue="Arbeidsongeschiktheid"
            disabled={true}
          />
          <Field
            name="isUnemploymentSelected"
            component={FormCheckbox}
            labelValue="Ook werkloosheid"
          />
        </div>
      </div>
      <div className={isUnemploymentSelected === YES ? 'flex-content flex-content_between' : 'flex-content flex-content_center'}>
        <div className="flex-content__row">
          <div className="flex-content-column">
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label id="maandbedrag" className="label-title">
              Welk maandbedrag wilt u verzekeren bij arbeidsongeschiktheid?
              <TafTooltip
                InfoText={MonthlyPaymentInfo}
                position={{ blockClass: 'mpi-block-pos', arrowClass: 'mp-arrow-pos' }}
                isShown={isMonthlyPaymentClicked}
                handleInfoClick={() => dispatch(triggerMonthlyPaymentInfoAction())}
              />
            </label>
            <Field
              disabled={false}
              id="maandbedrag"
              name="sumAssured"
              onKeyPress={onlyDigits}
              component={TafInput}
              isCurrency={true}
            />
          </div>
          {isUnemploymentSelected === YES &&
          <div className="flex-content-column">
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label className="label-title balancer">
              Welk maandbedrag wilt u verzekeren bij werkloosheid?
              <TafTooltip
                InfoText={UnemploymentInsuranceInfo}
                position={{ blockClass: 'uii-block-pos', arrowClass: 'uii-arrow-pos' }}
                isShown={isUnemploymentInsuranceInfo}
                handleInfoClick={() => dispatch(triggerUnemploymentInsuranceInfoAction())}
              />
            </label>
            <Field
              disabled={false}
              id="werkloosheid"
              name="unemploymentSumAssured"
              label=""
              component={TafInput}
              onKeyPress={onlyDigits}
              isLabel={true}
              isCurrency={true}
            />
          </div>}
        </div>
        <div className="flex-content__row">
          <div className="flex-content-column">
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label id="arbeidsongeschikt" className="label-title">
              Hoelang wilt u een uitkering ontvangen als u arbeidsongeschikt bent?
              <TafTooltip
                InfoText={DisabilityPaymentPeriodInfo}
                position={{ blockClass: 'dpp-block-pos', arrowClass: 'dpp-arrow-pos' }}
                isShown={isDisabilityPeriodInfo}
                handleInfoClick={() => dispatch(triggerDisabilityPeriodInfoAction())}
              />
            </label>
            <Field
              name="claimDuration"
              component={renderDropdownList}
              data={insurancePeriods}
            />
          </div>
          {isUnemploymentSelected === YES &&
          <div className="flex-content-column">
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label className="label-title">
              Hoelang wilt u een uitkering ontvangen als u werkloos bent?
              <TafTooltip
                InfoText={UnemploymentPeriodInfo}
                position={{ blockClass: 'mpi-block-pos', arrowClass: 'mp-arrow-pos' }}
                isShown={isUnemploymentPeriodInfo}
                handleInfoClick={() => dispatch(triggerUnemploymentPeriodInfoAction())}
              />
            </label>
            <Field
              name="unemploymentClaimDuration"
              component={renderDropdownList}
              data={unemploymentPeriods}
            />
          </div>}
        </div>
      </div>
      <div className="static-content">
        <div className="row">
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label className="label-title inputField">
            Selecteer de eigen risico periode?
            <TafTooltip
              InfoText={DisabilityPeriodInfo}
              position={{ blockClass: 'dpi-block-pos', arrowClass: 'dpi-arrow-pos' }}
              isShown={isPaymentPeriodInfo}
              handleInfoClick={() => dispatch(triggerPaymentPeriodInfoAction())}
            />
          </label>
          <Field
            name="waitingPeriod"
            component={renderDropdownList}
            data={residualPeriods}
            valueField="value"
            textField="periode"
          />
        </div>
        <div className="row">
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label id="geboortedatum" className="label-title inputField">
            Wat is uw geboortedatum?
          </label>
          <Field
            name="dateOfBirthDay"
            showTime={false}
            component={renderDateTimePicker}
            defaultCurrentDate={moment().subtract(30, 'years')
              .toDate()}
            placeholder="dd-mm-jjjj"
          />
        </div>
        <div className="row">
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label id="Einddatum" className="label-title inputField">
            Tot welke datum wilt u verzekerd zijn?
            <TafTooltip
              InfoText={InsuranceEndDateInfo}
              position={{ blockClass: 'isd-block-pos', arrowClass: 'isd-arrow-pos' }}
              isShown={isInsuranceEndDateInfo}
              handleInfoClick={() => dispatch(triggerInsuranceStartDateInfoAction())}
            />
          </label>
          <Field
            name="endDate"
            id="Einddatum"
            showTime={false}
            component={renderDateTimePicker}
            placeholder="dd-mm-jjjj"
            defaultCurrentDate={moment().add(30, 'years')
              .toDate()}
          />
        </div>
        <div className="row">
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label id="gedeeltelijke" className="label-title inputField">
            Bij gedeeltelijke arbeidsongeschiktheid, wilt
            u dan een uitkering naar rato of volledige uitkering ontvangen?
            <TafTooltip
              InfoText={PartlyDisabledInfo}
              position={{ blockClass: 'pdi-block-pos', arrowClass: 'pdi-arrow-pos' }}
              isShown={isPartlyDisabledInfo}
              handleInfoClick={() => dispatch(triggerPartlyDisabledInfoAction())}
            />
          </label>
          <Field
            name="paymentLevel"
            component={renderDropdownList}
            data={allowanceTypes}
          />
        </div>
        <div className="row">
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label className="label-title inputField">
            Beoordeling bij arbeidsongeschiktheid?
            <TafTooltip
              InfoText={DisabilityAssessmentInfo}
              position={{ blockClass: 'dai-block-pos', arrowClass: 'dai-arrow-pos' }}
              isShown={isDisabilityAssessmentInfo}
              handleInfoClick={() => dispatch(triggerDisabilityAssessmentInfoAction())}
            />
          </label>
          <Field
            name="claimAssessment"
            component={renderDropdownList}
            data={disabilityRates}
            valueField="value"
            textField="arbeidsongeschiktheid"
          />
        </div>
        <div className="row">
          {/* eslint-disable-next-line jsx-a11y/label-has-for */}
          <label className="label-title inputField">
            Ingangsdatum
          </label>
          <Field
            name="startDate"
            showTime={false}
            component={renderDateTimePicker}
            disabled={true}
            min={nextMonthFirstDay.toDate()}
            max={maxPossibleDate}
          />
        </div>
        <div>
          <button
            className="submit-btn"
            type="submit"
          >
            Berekenen
          </button>
        </div>
      </div>
    </form>
  );
};

FormContent = reduxForm({
  form: FORM_NAME,
  validate,
  onSubmitFail: (values, dispatch) => {
    dispatch(validationErrorAction());
  },
})(FormContent);


export default FormContent;


FormContent.propTypes = {
  handleSubmit: PropTypes.func,
};
